import { useEffect, useMemo, useRef, useState } from "react";
import Icon from "./Icon";
import GrupoTareas from "./misTareas/GrupoTareas";
import { NotionLogo } from "./widgets/NotionOrigen";
import HechasPanel from "./misTareas/HechasPanel";
import FilaTarea from "./misTareas/FilaTarea";
import useMisTareas, { GRUPOS } from "../hooks/useMisTareas";
import useNotionAlDia from "../hooks/useNotionAlDia";
import NotionAlDia from "./misTareas/NotionAlDia";
import ElegirListaNotion from "./misTareas/ElegirListaNotion";
import NuevasDeNotion from "./misTareas/NuevasDeNotion";
import { etiquetaDia } from "./misTareas/planes";
import * as api from "../api";
import EnviadasTab from "./misTareas/EnviadasTab";
import EquipoTab from "./misTareas/EquipoTab";
import PorAprobarTab from "./misTareas/PorAprobarTab";
import PropuestasRecibidas from "./misTareas/PropuestasRecibidas";
import MandarTareaModal from "./misTareas/MandarTareaModal";
import NoPuedoModal from "./misTareas/NoPuedoModal";
import HiloEncargo from "./misTareas/HiloEncargo";

// «Mis tareas»: lo que la persona tiene que hacer, ordenado y manejable.
// Arriba «Para hoy» (maximo 5) y debajo los grupos por vencimiento; Vencidas y Hoy se
// abren solos, el resto va plegado. Cada tarea tiene una sola accion principal (✓) y
// el resto en «Más». Los grupos y el orden los pone el servidor.
// notionConectado: sin Notion conectado no se ofrece «Traer tareas de Notion» (llevaria a una pestana vacia): se
// ofrece conectarlo. Quien no lo sepa deja el valor por defecto (conectado).
// equiposMios: nombres de los equipos de quien mira (para agrupar primero el suyo al mandar una tarea).
// Proponer (T-136): quien no es gestor ni admin propone en vez de mandar. `propuestasA` = las personas a las que puede proponer
// (el mismo, con el nombre «Yo mismo», y su equipo); `puedeAprobar` = es gestor o admin (ve «Por aprobar»); `miId` = su id.
export default function MisTareasPage({ equiposMios = [], propuestasA = [], puedeAprobar = false, miId = null, onAbrirProyecto, onTraerNotion, onConectarNotion, notionConectado = true, onConectarMicrosoft, showToast, hiloInicial = null, onHiloInicialVisto }) {
  const { datos, estado, cargar, actuar, planificar, recordatorio } = useMisTareas(true);
  // Notion al abrir: si lleva mas de 4 h sin sincronizar, el servidor lo hace en segundo plano y la lista se refresca sola
  useNotionAlDia(datos?.notion, cargar, estado === "ok");
  const [anuncio, setAnuncio] = useState("");
  const [verHechas, setVerHechas] = useState(false);
  const hoy = useMemo(() => new Date(), []);

  // Encargos: mandar tareas a mi equipo. Sin receptores (sin equipo o sin permiso) no hay pestanas ni
  // boton: se ve solo «Mias», como antes. El servidor decide quien puede; esto solo no ofrece lo que daria 403.
  const [receptores, setReceptores] = useState([]);
  const [receptoresInfo, setReceptoresInfo] = useState({ total: null, alcance: "equipo" });
  const [pestana, setPestana] = useState("mias");
  const [mandando, setMandando] = useState(false);
  const [eligiendoLista, setEligiendoLista] = useState(false);
  const [recargaNuevas, setRecargaNuevas] = useState(0);
  const [proponiendo, setProponiendo] = useState(null);   // null = cerrado; {} = abierto; {titulo, descripcion} = «Proponer otra»
  const [porAprobarN, setPorAprobarN] = useState(0);
  const [noPuedo, setNoPuedo] = useState(null);
  const [hilo, setHilo] = useState(null);
  const [recargaEnv, setRecargaEnv] = useState(0);
  useEffect(() => {
    let vivo = true;
    api.getReceptoresEncargo()
      .then(d => {
        if (!vivo) return;
        setReceptores(Array.isArray(d?.receptores) ? d.receptores : []);
        setReceptoresInfo({ total: Number.isFinite(d?.total) ? d.total : null, alcance: d?.alcance === "todos" ? "todos" : "equipo" });
      })
      .catch(() => { if (vivo) setReceptores([]); });
    return () => { vivo = false; };
  }, []);
  const puedeMandar = receptores.length > 0;
  // Quien no puede mandar pero si proponer (un member con equipo o consigo mismo)
  const puedeProponer = !puedeMandar && propuestasA.length > 0;
  const hayEnviadas = puedeMandar || puedeProponer;
  const pestanas = [["mias", "Mías"], ...(hayEnviadas ? [["enviadas", "Enviadas"]] : []), ...(puedeMandar ? [["equipo", "Equipo"]] : []),
                    ...(puedeAprobar ? [["porAprobar", "Por aprobar"]] : [])];
  const conPestanas = pestanas.length > 1;
  // El contador de «Por aprobar»: cuantas propuestas esperan a esta persona
  const contarPorAprobar = () => { api.getPorAprobar().then(d => setPorAprobarN(Number(d?.total) || 0)).catch(() => {}); };
  useEffect(() => { if (puedeAprobar) contarPorAprobar(); }, [puedeAprobar, recargaEnv]); // eslint-disable-line
  // Un aviso de la campana de un encargo abre su hilo, en cualquier pestana
  useEffect(() => {
    if (!hiloInicial) return;
    setHilo({ id: String(hiloInicial), titulo: "Tarea enviada" });
    onHiloInicialVisto?.();
  }, [hiloInicial]);

  const accion = async (t, cambios, mensaje) => {
    try {
      const r = await actuar(t, cambios);
      setAnuncio(mensaje || "Hecho");
      if (r?.aviso) showToast?.(r.aviso, "warn");
    } catch (e) {
      showToast?.(e?.message || "No se ha podido guardar el cambio", "error");
    }
  };
  // «Deshacer» durante 8 s tras marcar hecha. En un paso de proyecto reabre tambien el paso
  // en el proyecto (si la persona puede editarlo; si no, el servidor lo dice y se muestra).
  const [aviso, setAviso] = useState(null);
  const temporizador = useRef(null);
  useEffect(() => () => clearTimeout(temporizador.current), []);
  // Hecha y Descartada se resuelven igual: aviso con «Deshacer» 8 s, que las deja de nuevo pendientes
  const resolver = async (t, estado) => {
    const verbo = estado === "descartada" ? "Descartada" : "Hecha";
    try {
      const r = await actuar(t, { estado });
      setAnuncio(`${verbo}: ${t.titulo}`);
      if (r?.aviso) showToast?.(r.aviso, "warn");
      if (r?.recordatorio && r.recordatorio.estado !== "ok" && r.recordatorio.estado !== "ninguno") {
        showToast?.(`La tarea está ${verbo.toLowerCase()}, pero no se ha podido actualizar su recordatorio en To Do`, "warn");
      }
      clearTimeout(temporizador.current);
      if (r?.id) {
        setAviso({ titulo: t.titulo, id: r.id, verbo });
        temporizador.current = setTimeout(() => setAviso(null), 8000);
      } else setAviso(null);
    } catch (e) {
      showToast?.(e?.message || "No se ha podido guardar el cambio", "error");
    }
  };
  // Recuperar desde «Ver hechas»: devuelve true si salio bien (el panel se recarga)
  const recuperar = async t => {
    try {
      const r = await actuar({ id: t.id }, { estado: "pendiente" });
      setAnuncio(`Recuperada: ${t.titulo}`);
      if (r?.aviso) showToast?.(r.aviso);
      return true;
    } catch (e) {
      showToast?.(e?.message || "No se ha podido recuperar", "error");
      return false;
    }
  };
  const deshacer = async () => {
    const a = aviso;
    clearTimeout(temporizador.current); setAviso(null);
    if (!a) return;
    try {
      await actuar({ id: a.id }, { estado: "pendiente" });
      setAnuncio(`Recuperada: ${a.titulo}`);
    } catch (e) {
      showToast?.(e?.message || "No se ha podido deshacer", "error");
    }
  };
  const mantener = async t => {
    try { await api.mantenerMisTarea(t.id); setAnuncio(`Te quedas con: ${t.titulo}`); showToast?.("Te la quedas: ya no depende de Notion"); }
    catch (err) { showToast?.(err?.message || "No se ha podido conservar la tarea", "error"); }
    await cargar();
  };
  const hecha = t => resolver(t, "hecha");
  const otra = (t, cambios) => (cambios && cambios.estado === "descartada" ? resolver(t, "descartada") : accion(t, cambios, "Tarea actualizada"));

  const enviarNoPuedo = async motivo => {
    await api.noPuedoEncargo(noPuedo.encargo.id, motivo);
    setAnuncio(`Respondido «No puedo»: ${noPuedo.titulo}`);
    setNoPuedo(null);
    await cargar();
  };
  const enviada = (r, receptor) => {
    setMandando(false);
    setRecargaEnv(x => x + 1);
    setAnuncio(`Tarea mandada a ${receptor.nombre}`);
    showToast?.(`Tarea mandada a ${receptor.nombre}`);
  };

  // Planificar: los errores (DIA_LLENO...) los gestiona el menu; aqui solo se anuncia.
  const planear = async (t, cuerpo) => {
    const r = await planificar(t, cuerpo);
    setAnuncio(cuerpo.tipo === "ninguno" ? `Quitada del plan: ${t.titulo}` : `Planificada: ${t.titulo}`);
    const est = r?.recordatorio?.estado;
    if (est && est !== "ok" && est !== "ninguno") showToast?.("La tarea está planificada, pero no se ha podido actualizar el recordatorio de To Do", "warn");
    return r;
  };
  // Planificar desde el menu «Más» (el menu Planificar gestiona sus propios errores)
  const planearRapido = async (t, cuerpo) => {
    try { await planear(t, cuerpo); }
    catch (err) { showToast?.(err?.message || "No se ha podido planificar", "error"); }
  };
  const poner = async (t, cuerpo) => {
    const r = await recordatorio(t.id, cuerpo);
    if (r?.recordatorio?.estado === "ok") setAnuncio(`Recordatorio en To Do: ${t.titulo}`);
    return r;
  };
  const quitarRecordatorio = async t => {
    try { await recordatorio(t.id, null); setAnuncio(`Recordatorio quitado: ${t.titulo}`); }
    catch (err) { showToast?.(err?.message || "No se ha podido quitar el recordatorio", "error"); }
  };

  if (estado === "cargando") {
    return <p className="mt-estado-pag" role="status">Cargando tus tareas…</p>;
  }
  if (estado === "no-disponible") {
    return (
      <div className="mt-vacio" role="status">
        <b>«Mis tareas» aún no está disponible para ti</b>
        Por ahora no puedes verla. Mientras tanto, tus pasos siguen en cada proyecto.
      </div>
    );
  }
  if (estado === "error" || !datos) {
    return (
      <div className="mt-vacio" role="alert">
        <b>No se han podido cargar tus tareas</b>
        <button type="button" className="btn mt-reintentar" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const g = datos.grupos;
  const hayAlgo = datos.mi_dia.length > 0 || GRUPOS.some(x => g[x.clave].total > 0);
  const plan = datos.plan;
  const props = { onHecha: hecha, onAccion: otra, onAbrirProyecto, hoy,
    onNoPuedo: setNoPuedo, onMantener: mantener, onHilo: t => setHilo({ id: t.encargo.id, titulo: t.titulo }),
    ...(plan ? { conPlan: true, conteo: plan.conteo, tope: plan.tope, onPlan: planear, onPlanRapido: planearRapido, onRecordatorio: poner,
                 onQuitarRecordatorio: quitarRecordatorio, onConectarMicrosoft } : {}) };
  const hayAlgoPlan = !!plan && (plan.hoy.total + plan.semana.total + plan.semana_siguiente.total + plan.sin_planificar.total > 0
    || plan.dias.some(d => d.total > 0) || g.aplazadas.total > 0);

  return (
    <div className="mt-pagina">
      <div className="mt-cab">
        <h2 className="mt-h">Tareas</h2>
        {/* Los contadores solo dicen algo si alguno no es cero: el primer dia, tres ceros son ruido */}
        {g.vencidas.total + g.hoy.total + g.semana.total > 0 && (
          <div className="mt-cont" aria-label="Resumen">
            <span className="mt-cn rojo"><b>{g.vencidas.total}</b> vencidas</span>
            <span className="mt-cn acento"><b>{g.hoy.total}</b> para hoy</span>
            <span className="mt-cn"><b>{g.semana.total}</b> esta semana</span>
          </div>
        )}
        {notionConectado && (
          <button type="button" className="mt-b btn" onClick={() => setEligiendoLista(true)}>
            <NotionLogo title="" />Mi lista de Notion
          </button>
        )}
        {puedeMandar && (
          <button type="button" className="mt-b p btn mt-mandar" onClick={() => setMandando(true)}>
            <Icon name="person" size={13} color="currentColor" />Mandar tarea
          </button>
        )}
        {puedeProponer && (
          <button type="button" className="mt-b p btn mt-mandar" onClick={() => setProponiendo({})}>
            <Icon name="person" size={13} color="currentColor" />Proponer tarea
          </button>
        )}
      </div>
      <NotionAlDia notion={datos.notion} onReconectar={onConectarNotion}
        onSincronizar={async () => {
          // El servidor comparte cerrojo con la sincronizacion automatica: si ya hay una en curso, se une a ella
          try { await api.syncNotion(); setAnuncio("Notion sincronizado"); }
          catch (err) { showToast?.(err?.message || "No se ha podido sincronizar Notion", "error"); }
          await cargar();
        }} />
      {conPestanas && (
        <div className="mt-tabs" role="tablist" aria-label="Tareas">
          {pestanas.map(([k, t]) => (
            <button key={k} type="button" role="tab" id={`mt-tab-${k}`} aria-selected={pestana === k}
              className={"btn" + (pestana === k ? " on" : "")}
              onClick={() => setPestana(k)}>{t}{k === "porAprobar" && porAprobarN > 0 && <b className="mt-cont2">{porAprobarN}</b>}</button>
          ))}
        </div>
      )}
      {eligiendoLista && (
        <ElegirListaNotion onCerrar={() => { setEligiendoLista(false); setRecargaNuevas(x => x + 1); }} onConectar={onConectarNotion} showToast={showToast}
          onCambio={() => { setRecargaNuevas(x => x + 1); cargar(); }}
          onGuardada={f => { setEligiendoLista(false); setAnuncio("Lista de Notion guardada"); showToast?.(`Lista «${f.nombre}» guardada: ${f.mias?.n ?? 0} tareas tuyas`); setRecargaNuevas(x => x + 1); cargar(); }} />
      )}
      {proponiendo && (
        <MandarTareaModal modo="proponer" receptores={propuestasA} miId={miId} inicial={proponiendo} onCerrar={() => setProponiendo(null)}
          onEnviada={() => { setProponiendo(null); setRecargaEnv(x => x + 1); cargar(); setAnuncio("Tarea propuesta"); showToast?.("Propuesta enviada: un gestor o administrador tendrá que aprobarla"); }} />
      )}
      {mandando && <MandarTareaModal receptores={receptores} total={receptoresInfo.total} alcance={receptoresInfo.alcance} equiposMios={equiposMios} onCerrar={() => setMandando(false)} onEnviada={enviada} />}
      {noPuedo && <NoPuedoModal tarea={noPuedo} onCerrar={() => setNoPuedo(null)} onEnviar={enviarNoPuedo} />}
      {hilo && <HiloEncargo encargoId={hilo.id} titulo={hilo.titulo} onCerrar={() => setHilo(null)} onComentado={cargar} />}
      {conPestanas && pestana !== "mias" && (
        <div role="tabpanel" id="mt-panel" aria-labelledby={`mt-tab-${pestana}`}>
          {pestana === "enviadas" ? (
            <EnviadasTab recarga={recargaEnv} miId={miId}
              etiquetaMandar={puedeProponer ? "Proponer tarea" : "Mandar tarea"}
              onMandar={() => (puedeProponer ? setProponiendo({}) : setMandando(true))}
              onProponerOtra={puedeProponer ? e => setProponiendo({ titulo: e.titulo, descripcion: e.descripcion || "" }) : undefined} />
          ) : pestana === "porAprobar" ? (
            <PorAprobarTab miId={miId} showToast={showToast} onCambio={contarPorAprobar} />
          ) : <EquipoTab />}
        </div>
      )}


      {(!conPestanas || pestana === "mias") && <NuevasDeNotion recarga={recargaNuevas} showToast={showToast} onAnadidas={cargar} />}
      {(!conPestanas || pestana === "mias") && <PropuestasRecibidas items={datos.propuestas} miId={miId} onHilo={t => setHilo({ id: t.encargo.id, titulo: t.titulo })} />}
      {(!conPestanas || pestana === "mias") && (!(plan ? hayAlgoPlan : hayAlgo) ? (
        <div className="mt-vacio">
          <Icon name="check-circle-fill" size={30} color="var(--accent)" />
          <b>Aquí irán tus tareas</b>
          Verás los pasos de proyecto que tengas asignados como encargado o responsable
          {notionConectado ? ", y las páginas de Notion que elijas traer." : " y las tareas que te manden."}
          {!notionConectado && " Si trabajas en Notion, conéctalo para traer tus páginas."}
          {notionConectado && onTraerNotion && (
            <div>
              <button type="button" className="mt-vacio-enlace" onClick={onTraerNotion}>
                <NotionLogo title="Notion" />Traer tareas de Notion
              </button>
            </div>
          )}
          {!notionConectado && onConectarNotion && (
            <div>
              <button type="button" className="mt-vacio-enlace" onClick={onConectarNotion}>
                <NotionLogo title="Notion" />Conectar Notion
              </button>
            </div>
          )}
        </div>
      ) : (
        plan ? (
          <>
            <GrupoTareas etiqueta="Hoy" total={plan.hoy.total} items={plan.hoy.items} detalle={` de ${plan.tope}`}
              abiertoInicial vacio="Nada para hoy. Usa «Planificar» en una tarea para ponerla aquí." {...props} />
            {plan.dias.map(d => (
              <GrupoTareas key={d.dia} etiqueta={etiquetaDia(d.dia) + (d.pasado ? " · pasado" : "")}
                total={d.total} items={d.items} abiertoInicial {...props} />
            ))}
            <GrupoTareas etiqueta="Esta semana (sin día)" total={plan.semana.total} items={plan.semana.items}
              abiertoInicial {...props} />
            <GrupoTareas etiqueta="Semana que viene (sin día)" total={plan.semana_siguiente.total} items={plan.semana_siguiente.items}
              abiertoInicial={plan.semana_siguiente.total > 0}
              vacio="Nada para la semana que viene. Usa «Planificar» y elige «Semana que viene»." {...props} />
            <GrupoTareas etiqueta="Sin planificar" total={plan.sin_planificar.total} items={plan.sin_planificar.items}
              abiertoInicial {...props} />
            <GrupoTareas etiqueta="Aplazadas" total={g.aplazadas.total} items={g.aplazadas.items} {...props} />
          </>
        ) : (
        <>
          <section className="mt-dia" aria-labelledby="mt-dia-t">
            <div className="mt-dia-t" id="mt-dia-t">
              <Icon name="sun" size={14} color="currentColor" />Para hoy
              <span>{datos.mi_dia.length} de 5 · lo más urgente</span>
            </div>
            {datos.mi_dia.length
              ? datos.mi_dia.map(t => <FilaTarea key={t.id ?? `p${t.task_id}`} tarea={t} {...props} />)
              : <div className="mt-vacio-grupo">Nada urgente. Fija tareas aquí desde «Más».</div>}
          </section>
          {GRUPOS.map(x => (g[x.clave].total > 0 || x.clave === "aplazadas") && (
            <GrupoTareas key={x.clave} etiqueta={x.etiqueta} total={g[x.clave].total} items={g[x.clave].items}
              abiertoInicial={!!x.abierto} alerta={!!x.alerta} {...props} />
          ))}
        </>
        )
      ))}
      {/* «Ver hechas» es secundario: al final de la lista, con su panel justo encima */}
      {verHechas && <HechasPanel onRecuperar={recuperar} onCerrar={() => setVerHechas(false)} />}
      <div className="mt-pie-hechas">
        <button type="button" className="mt-hechas mt-ver-hechas" aria-expanded={verHechas}
          onClick={() => setVerHechas(v => !v)}>Ver hechas</button>
      </div>
      <div className="mt-vivo" role="status" aria-live="polite">{anuncio}</div>
      {aviso && (
        <div className="mt-deshacer">
          <span>{aviso.verbo || "Hecha"}: {aviso.titulo}</span>
          <button type="button" onClick={deshacer}>Deshacer</button>
        </div>
      )}
    </div>
  );
}
